/*
    Vanilla replacements for the Iguana getters on Lesson.Stream that the stream dashboard reads.

    `orderedLessons` is chapter order, which is not the same as the order of `stream.lessons`.
    Several of the Iguana getters iterate `stream.lessons` instead, and where they do, these
    functions do too, so the counts match.
*/
import { type CurrentUser } from 'Users';
import { type LessonForStreamDashboard, type StreamForStreamDashboard, type StreamSummary } from 'Lessons';
import { type AnyObject } from '@Types';
import { type ImageInterface } from 'Image/types';
import { originalUrl, sizedUrl } from 'hasResizableImages';
import { getMsLeftFromStreamProgress } from './timeLimitSelectors';
import { type LessonKeyTerms } from '../StreamDashboard.types';
import {
    getChapterForLesson,
    getChapterLessons,
    getComingSoonLessonsInChapter,
    isLessonComingSoon,
} from './chapterSelectors';
import { getKeyTermsForDisplay, getLessonLastProgressAt, isLessonComplete } from './lessonSelectors';

/** Mirrors Lesson#comingSoon, which resolves the flag through the lesson's chapter. */
export const isLessonComingSoonInStream = (stream: StreamForStreamDashboard, lesson: LessonForStreamDashboard) => {
    const chapter = getChapterForLesson(stream, lesson);
    return !!chapter && isLessonComingSoon(chapter, lesson);
};

/** Mirrors Stream#orderedLessons: every chapter's lessons, in chapter order. */
export const getOrderedLessons = (stream: StreamForStreamDashboard): LessonForStreamDashboard[] =>
    stream.chapters.flatMap(chapter => getChapterLessons(stream, chapter));

/** Mirrors Stream#comingSoonLessons. */
export const getComingSoonLessons = (stream: StreamForStreamDashboard): LessonForStreamDashboard[] =>
    stream.chapters.flatMap(chapter => getComingSoonLessonsInChapter(stream, chapter));

export const isStreamStarted = (stream: StreamForStreamDashboard) => !!stream.lessonStreamsProgress;

export const isStreamComplete = (stream: StreamForStreamDashboard) => !!stream.lessonStreamsProgress?.complete;

/** Mirrors Stream#hasLessonProgress, which looks for any lesson with a lastProgressAt. */
export const hasLessonProgress = (stream: StreamForStreamDashboard) =>
    stream.lessons.some(lesson => !!getLessonLastProgressAt(lesson));

/** Mirrors Stream#completeExcludingComingSoonLessons. */
export const isCompleteExcludingComingSoonLessons = (stream: StreamForStreamDashboard) =>
    stream.lessons.every(lesson => isLessonComplete(lesson) || isLessonComingSoonInStream(stream, lesson));

/** Mirrors Stream#getKeyTerms, grouped by lesson. Coming-soon lessons and lessons with no key terms are left out. */
export const getKeyTermsByLesson = (
    stream: StreamForStreamDashboard,
    stripFormatting: (text: string) => string,
): LessonKeyTerms[] =>
    stream.chapters.flatMap((chapter, chapterIndex) =>
        getChapterLessons(stream, chapter)
            .filter(lesson => !isLessonComingSoonInStream(stream, lesson))
            .map(lesson => ({ chapterIndex, lesson, keyTerms: getKeyTermsForDisplay(lesson, stripFormatting) }))
            .filter(entry => entry.keyTerms.length > 0),
    );

/**
 * Mirrors Stream.keepLearningLesson. `canLaunch` is injected because access rules live in the
 * ContentAccess module and depend on the current user, which would make this impure.
 */
export const getKeepLearningLesson = (
    stream: StreamForStreamDashboard,
    canLaunch: (lesson: LessonForStreamDashboard) => boolean,
): LessonForStreamDashboard | undefined => {
    let keepLearningLesson: LessonForStreamDashboard | undefined;

    getOrderedLessons(stream).forEach(lesson => {
        if (!canLaunch(lesson)) return;

        // A complete or coming-soon lesson is never the keep-learning lesson, but an unstarted
        // available one can be.
        if (isLessonComplete(lesson) || isLessonComingSoonInStream(stream, lesson)) return;

        if (!keepLearningLesson) {
            keepLearningLesson = lesson;
            return;
        }

        const lastProgressAt = getLessonLastProgressAt(lesson);
        const bestLastProgressAt = getLessonLastProgressAt(keepLearningLesson);

        // Progress beats no progress. With neither, the earlier lesson in the stream wins.
        if (lastProgressAt && !bestLastProgressAt) {
            keepLearningLesson = lesson;
            return;
        }
        if (!lastProgressAt) return;

        if (bestLastProgressAt && lastProgressAt > bestLastProgressAt) keepLearningLesson = lesson;
    });

    return keepLearningLesson;
};

export const getStreamImageSrc = (stream: StreamForStreamDashboard) => originalUrl(stream.image);

/*
    Mirrors StreamProgress#_certificateDimensions, which asks the image-resizer worker for a
    rendition sized to the device pixel ratio rather than the original at full resolution.
*/
const certificateDimensionsForScreen = (): [number, number] => {
    if (window.devicePixelRatio > 2) return [2100, 825];
    if (window.devicePixelRatio > 1) return [1400, 550];
    return [700, 275];
};

/**
 * Mirrors StreamProgress#certificateImageSrc. StreamProgress#certificateImage is typed as an untyped
 * object in ContentProgress (see the FIXME there), so we narrow it here.
 */
export const getCertificateImageSrc = (stream: StreamForStreamDashboard) => {
    const certificateImage = stream.lessonStreamsProgress?.certificateImage as ImageInterface | null | undefined;
    if (!certificateImage) return undefined;

    const [width, height] = certificateDimensionsForScreen();
    return sizedUrl(certificateImage, width, height) || undefined;
};

/** Mirrors EntityMetadata#imageSrc. */
export const getEntityMetadataImageSrc = (stream: StreamForStreamDashboard) => {
    const image = stream.entityMetadata?.image;
    return image ? originalUrl(image) : undefined;
};

/** Mirrors Stream#logInfo. The keys stay snake_cased because they go straight to EventLogger. */
export const getStreamLogInfo = (stream: StreamForStreamDashboard): AnyObject => ({
    lesson_stream_id: stream.id,
    lesson_stream_complete: isStreamComplete(stream),
    lesson_stream_version_id: stream.versionId,
    exam: stream.exam,
});

/**
 * Mirrors StreamProgress#inProgress: a stream whose timer has run out is not in progress.
 */
export function isStreamInProgress(stream: StreamForStreamDashboard) {
    const progress = stream.lessonStreamsProgress;
    if (!progress) return false;
    if (progress.complete) return false;
    if (getMsLeftFromStreamProgress(progress) === 0) return false;
    return !!progress.startedAt;
}

/*
    Mirrors StreamSummariesHelper#summariesForStreamOrLesson.

    Two behaviours worth keeping: without a user it returns nothing at all, and when the stream cannot
    be launched it returns an empty list. So a visitor sees no summaries, and a student who has not
    unlocked the course sees none either.

    Stream.StreamSummariesHelper itself stays where it is; player_view_model_with_lesson still uses it.
*/
export const getStreamSummaries = (
    stream: StreamForStreamDashboard,
    user: CurrentUser | null,
    canLaunchStream: boolean,
): StreamSummary[] => {
    if (!user) return [];
    if (!canLaunchStream) return [];
    return stream.summaries;
};

// A title match, as the old Iguana getter was. Do not change it: the completion share copy depends on it.
export const isBlueOceanStream = (stream: StreamForStreamDashboard) => stream.title === 'Blue Ocean Strategy';
